// 발바닥 접지: Hips/root/상체를 옮기지 않고 두 다리 본의 회전만 보정한다.
import * as THREE from 'three/webgpu';

const CONTACT_MARGIN = 0.006;
const RELEASE_BAND = 0.012;
const MAX_LIFT = 0.24;
/** 디딤 발 수평 고정의 최대 거리(월드 m). 넘으면 고정점을 이 거리까지 끌고 간다(회전 피벗에서 다리를 늘이지 않고, 놓았다가 한꺼번에 끌려가지 않게). */
const MAX_LOCK = 0.2;
/** 고정을 푼 뒤 남은 수평 보정이 줄어드는 빠르기(1/영웅 초): 0.1초에 95% */
const LOCK_RELEASE = 30;
/**
 * 회전 디딤(2026-10-20, D09). 게임은 이동 방향을 한 틱에 바꾸고 몸은 약 0.1초에 걸쳐 도는데, 그동안 달리기 클립의 디딤 발은
 * 바닥 기준 최대 14m/s로 움직여 MAX_LOCK 끌기 고정이 매 프레임 포화됐다(60Hz 180도 p90 41m/s).
 * 회전 중 디딤은 끌지 않고 바닥에 붙여 두고, 다리가 다 펴지기 전(REACH)이나 STEP_MAX를 넘으면 발을 디딤 띠 위로 들어 옮겨 딛는다.
 * 옮기기: 높이는 걸음 전체에 사인 곡선(0→최고→0)으로 들고 내린다. 앞 STEP_MOVE_START까지는 제자리에서 들고,
 * 높이가 최고의 59% 이상인 동안 보이는 자리에서 현재 클립 발 자리로 옮기고, STEP_MOVE_END부터는 그 자리를 바닥에 고정한 채 내린다.
 * (처음 판은 최고 높이를 걸음의 15%에 들고 내려 0.1초 걸음에서 발이 10m/s로 꽂혀 멈춤 첫 프레임 엉덩이 관절이 튀었다, 2026-10-20 남동 90도.)
 */
export const STEP_TIME = 0.1;
const STEP_MOVE_START = 0.2;
const STEP_MOVE_END = 0.8;
/** 옮겨 딛는 동안 디딤 발목 높이 위로 드는 높이(월드 m). 디딤 판정 띠(+0.06m)보다 충분히 높게 */
export const STEP_LIFT = 0.15;
/** 회전 디딤 고정의 최대 수평 거리(월드 m). 넘으면 옮겨 딛는다 */
export const STEP_MAX = 0.55;
/** 대기 정리 걸음: 멈춘 뒤 전환이 끝나고 고정점이 클립 발에서 이 거리(월드 m)를 넘으면 한 발씩 천천히 옮겨 딛는다 */
export const IDLE_SETTLE = 0.06;
/** 대기 정리 걸음 시간(영웅 초). 회전 옮겨 딛기(STEP_TIME)보다 느리게: 0.1초 걸음은 엉덩이 관절 1,317°/s(기준 872°/s)로 튀었다 */
export const IDLE_SETTLE_TIME = 0.25;
/** 엉덩이→고정점 거리가 다리 길이의 이 비율을 넘으면 옮겨 딛는다(IK가 닿지 못해 발이 끌려가기 전에) */
const REACH = 0.95;
/**
 * 한계·다리 길이로 옮겨 딛는 걸음의 최고 평균 속도(월드 m/s). 걸음 시간 = max(stepTime, 거리/이 값).
 * 2026-10-20: 회전 고정 0.55m를 0.1초에 옮기자 엉덩이 관절이 983°/s(대기·달리기 최대 708°/s의 1.39배), 4m/s(0.137초)에서도 915°/s로 튀었다.
 */
const STEP_SPEED = 3;

/** 0..1 → 0..1, 양 끝 기울기 0 */
function smooth01(x: number): number {
  const t = x <= 0 ? 0 : x >= 1 ? 1 : x;
  return t * t * (3 - 2 * t);
}

/** 발 수평 고정 입력. band는 다리별 "디딤" 발목 높이 상한(바닥 기준 월드 m). */
export interface FootLockInput {
  active: boolean;
  dt: number;
  band: readonly [number, number];
  /** 회전 디딤 세기(0..1, 기록용). */
  turn?: number;
  /**
   * 디딤 고정 방식. 0(기본)이면 MAX_LOCK까지 끌고 가는 달리기 고정, 0보다 크면 끌지 않고 고정했다가
   * 고정점이 클립 발에서 이 거리(월드 m)를 넘거나 다리가 다 펴지기 전에 발을 들어 옮겨 딛는다(회전·대기 STEP_MAX).
   * 이 방식으로 잡은 고정이 0 방식으로 넘어가면 MAX_LOCK 안이면 끌기 고정으로 바꾸고, 넘으면 옮겨 딛는다.
   */
  stepAt?: number;
  /** 0보다 크면 고정점이 이 거리를 넘을 때(한계 안이라도) 다른 발이 옮기는 중이 아니면 settleTime 동안 천천히 옮겨 딛는다 */
  settle?: number;
  settleTime?: number;
  /** 한계·다리 길이로 옮겨 딛는 시간(영웅 초)과 드는 높이(월드 m). 생략하면 회전 값(STEP_TIME·STEP_LIFT) */
  stepTime?: number;
  stepLift?: number;
  /** 대기 고정이 끝나는 프레임(대기→달리기 등). 고정을 풀고 보이는 발 자리에서 새로 잡는다(몸이 돌며 출발할 때 붙은 발로 엉덩이가 비틀리지 않게). */
  release?: boolean;
}

/** 0 근처에서 미분까지 연속인 양수 부분. 관통은 즉시 막고 접촉 해제 때 잔류 높이를 누적하지 않는다. */
export function contactLift(penetration: number): number {
  if (penetration <= -RELEASE_BAND) return 0;
  if (penetration >= RELEASE_BAND) return Math.min(MAX_LIFT, penetration);
  return (penetration + RELEASE_BAND) ** 2 / (4 * RELEASE_BAND);
}

class LegContact {
  readonly hip: THREE.Bone;
  readonly knee: THREE.Bone;
  readonly foot: THREE.Bone;
  /** 무릎 굽힘 기준(발끝 방향)을 읽는 발가락 본. 없으면 기준 없이 원래 무릎 방향만 쓴다 */
  readonly toe: THREE.Bone | null;
  readonly probes: number[] = [];
  readonly baseHip = new THREE.Quaternion();
  readonly baseKnee = new THREE.Quaternion();
  readonly baseFoot = new THREE.Quaternion();
  applied = false;
  /** 디딤 수평 고정: 고정점(월드 x,z)과 지금 더하는 보정(월드 m). blocked면 이번 디딤이 끝날 때까지 다시 고정하지 않는다. */
  locked = false;
  blocked = false;
  lockX = 0;
  lockZ = 0;
  offX = 0;
  offZ = 0;
  /** 회전 중에 잡은 고정(끌지 않음). 디딤이 끝나면 풀린다. */
  turnLocked = false;
  /** 옮겨 딛기 경과(영웅 초), 아니면 -1. 시작 자리(stepX/Z)와 내려놓을 자리(landX/Z, landed 뒤 고정) */
  stepT = -1;
  stepDur = 0.1;
  stepHeight = 0.15;
  stepX = 0;
  stepZ = 0;
  landX = 0;
  landZ = 0;
  landed = false;
  /** 이번 프레임 옮겨 딛기로 더하는 높이(월드 m) */
  stepLift = 0;
  constructor(bones: THREE.Bone[], side: string) {
    this.hip = bones.find(b => b.name.endsWith(side + 'UpLeg'))!;
    this.knee = bones.find(b => b.name.endsWith(side + 'Leg'))!;
    this.foot = bones.find(b => b.name.endsWith(side + 'Foot'))!;
    this.toe = bones.find(b => b.name.endsWith(side + 'ToeBase')) ?? null;
  }
}

export class GroundContact {
  private readonly legs: LegContact[];
  private readonly a = new THREE.Vector3();
  private readonly b = new THREE.Vector3();
  private readonly c = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly direction = new THREE.Vector3();
  private readonly bend = new THREE.Vector3();
  private readonly desiredKnee = new THREE.Vector3();
  private readonly from = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private readonly point = new THREE.Vector3();
  private readonly footWorld = new THREE.Quaternion();
  private readonly rotation = new THREE.Quaternion();
  private readonly parentRotation = new THREE.Quaternion();
  private readonly delta = new THREE.Quaternion();

  constructor(private readonly mesh: THREE.SkinnedMesh) {
    const bones = mesh.skeleton.bones;
    this.legs = [new LegContact(bones, 'Left'), new LegContact(bones, 'Right')];
    // 발/발가락을 따로 묶어 각 본 공간의 26방향 극점을 고른다. 로딩 때만 전체 정점을 순회한다.
    // 발목 중심이나 run 한 자세의 최저점만 쓰면 구르기 뒤집힌 발·발끝을 놓칠 수 있다.
    const ids = mesh.geometry.getAttribute('skinIndex'), weights = mesh.geometry.getAttribute('skinWeight');
    mesh.updateWorldMatrix(true, false); mesh.skeleton.update();
    const directions: THREE.Vector3[] = [];
    for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) {
      if (x || y || z) directions.push(new THREE.Vector3(x, y, z).normalize());
    }
    for (let side = 0; side < 2; side++) {
      const name = side ? 'Right' : 'Left', selected = new Set<number>();
      for (const part of ['Foot', 'ToeBase']) {
        const bone = bones.find(b => b.name.endsWith(name + part));
        if (!bone) continue;
        bone.updateWorldMatrix(true, false);
        const inverse = bone.matrixWorld.clone().invert();
        const best = new Float64Array(directions.length).fill(-Infinity), bestIndex = new Int32Array(directions.length).fill(-1);
        for (let i = 0; i < ids.count; i++) {
          let sum = 0, dominant = -1, max = 0;
          for (let k = 0; k < 4; k++) {
            const id = ids.getComponent(i, k), w = weights.getComponent(i, k), n = bones[id].name;
            if (n.includes(name) && /Foot|Toe/.test(n)) sum += w;
            if (w > max) { max = w; dominant = id; }
          }
          if (sum <= 0.5 || bones[dominant] !== bone) continue;
          mesh.getVertexPosition(i, this.point).applyMatrix4(mesh.matrixWorld).applyMatrix4(inverse);
          for (let d = 0; d < directions.length; d++) {
            const value = this.point.dot(directions[d]);
            if (value > best[d]) { best[d] = value; bestIndex[d] = i; }
          }
        }
        for (const i of bestIndex) if (i >= 0) selected.add(i);
      }
      this.legs[side].probes.push(...selected);
    }
  }

  /** mixer가 쓰기 전에 직전의 가산 회전을 제거한다. dt=0에서도 재적용이 누적되지 않는다. */
  restore(): void {
    for (const leg of this.legs) {
      if (!leg.applied) continue;
      leg.hip.quaternion.copy(leg.baseHip);
      leg.knee.quaternion.copy(leg.baseKnee);
      leg.foot.quaternion.copy(leg.baseFoot);
      leg.applied = false;
    }
  }

  /** 달리기 디딤 고정을 즉시 지운다(회피처럼 접지를 쓰지 않는 동작으로 넘어갈 때·장면 전환). */
  clearLocks(): void {
    for (const leg of this.legs) {
      leg.locked = leg.blocked = leg.turnLocked = false; leg.offX = leg.offZ = 0;
      leg.stepT = -1; leg.stepLift = 0;
    }
  }

  apply(floorY: number, lock?: FootLockInput): void {
    this.mesh.updateWorldMatrix(true, false);
    this.mesh.skeleton.update();
    for (let side = 0; side < 2; side++) {
      const leg = this.legs[side];
      let low = Infinity;
      for (const i of leg.probes) {
        this.mesh.getVertexPosition(i, this.point).applyMatrix4(this.mesh.matrixWorld);
        low = Math.min(low, this.point.y);
      }
      const lift = contactLift(floorY + CONTACT_MARGIN - low);
      if (lock) this.updateLock(leg, side, floorY, lock);
      else { leg.offX = leg.offZ = 0; leg.locked = leg.blocked = leg.turnLocked = false; leg.stepT = -1; leg.stepLift = 0; }
      const up = lift + leg.stepLift;
      if (up <= 1e-7 && leg.offX * leg.offX + leg.offZ * leg.offZ < 1e-10) continue;
      leg.baseHip.copy(leg.hip.quaternion); leg.baseKnee.copy(leg.knee.quaternion); leg.baseFoot.copy(leg.foot.quaternion);
      leg.applied = true;
      this.solve(leg, up, leg.offX, leg.offZ);
      this.mesh.skeleton.update();
    }
  }

  /** 발목(접지 보정 전) 높이로 디딤을 판정해 수평 보정을 정한다. 히트스톱(dt=0)에는 그대로 둔다. */
  private updateLock(leg: LegContact, side: number, floorY: number, lock: FootLockInput): void {
    if (lock.dt <= 0) return;
    leg.foot.getWorldPosition(this.c);
    const rel = this.c.y - floorY;
    const stance = lock.active && rel < lock.band[side];
    const stepAt = lock.stepAt ?? 0;
    const plant = stepAt > 0;
    leg.stepLift = 0;
    if (lock.release && leg.stepT < 0) { leg.locked = false; leg.turnLocked = false; }
    if (leg.stepT >= 0) {
      this.updateStep(leg, side, rel, stance, plant, lock);
      return;
    }
    if (!stance) { leg.blocked = false; leg.turnLocked = false; }
    if (stance && !leg.blocked) {
      // 남은 보정이 있으면 그 자리(보이는 발 위치)에서 고정해 이어지게 한다.
      if (!leg.locked) { leg.locked = true; leg.lockX = this.c.x + leg.offX; leg.lockZ = this.c.z + leg.offZ; }
      if (plant) leg.turnLocked = true;
      let ox = leg.lockX - this.c.x, oz = leg.lockZ - this.c.z;
      const len = Math.hypot(ox, oz);
      if (leg.turnLocked) {
        // 회전·대기 디딤: 끌지 않는다. 다리가 다 펴지기 전이나 한계 거리를 넘으면 들어 옮겨 딛는다.
        // 끌기 고정(0 방식)으로 넘어왔으면 한계는 STEP_MAX이고(바로 옮겨 딛지 않고 디딤이 끝날 때까지 잇는다), MAX_LOCK 안이면 끌기 고정으로 바꾼다.
        const settle = plant && (lock.settle ?? 0) > 0 && len > lock.settle! && !this.anyStepping();
        const limit = plant ? stepAt : STEP_MAX;
        if (len > limit || settle || this.overReach(leg, leg.lockX, this.c.y, leg.lockZ)) {
          leg.locked = false;
          leg.stepT = 0; leg.landed = false;
          leg.stepDur = settle && len <= limit ? lock.settleTime ?? STEP_TIME : Math.max(lock.stepTime ?? STEP_TIME, len / STEP_SPEED);
          leg.stepHeight = lock.stepLift ?? STEP_LIFT;
          leg.stepX = leg.lockX; leg.stepZ = leg.lockZ;
        } else if (!plant && len <= MAX_LOCK) leg.turnLocked = false;
      } else if (len > MAX_LOCK) {
        // 고정점을 발 쪽으로 끌어 거리를 MAX_LOCK에 묶는다(발은 넘친 만큼만 미끄러진다).
        ox *= MAX_LOCK / len; oz *= MAX_LOCK / len;
        leg.lockX = this.c.x + ox; leg.lockZ = this.c.z + oz;
      }
      leg.offX = ox; leg.offZ = oz;
      return;
    }
    leg.locked = false;
    const k = Math.exp(-LOCK_RELEASE * lock.dt);
    leg.offX *= k; leg.offZ *= k;
  }

  /** 옮겨 딛기 한 프레임: 들기 → 들린 채 옮기기 → 고정한 채 내리기. 끝나면 디딤이면 그 자리에 고정한다. */
  private updateStep(leg: LegContact, side: number, rel: number, stance: boolean, plant: boolean, lock: FootLockInput): void {
    leg.stepT += lock.dt;
    const u = Math.min(1, leg.stepT / leg.stepDur);
    let px: number, pz: number;
    if (u < STEP_MOVE_END) {
      const s = smooth01((u - STEP_MOVE_START) / (STEP_MOVE_END - STEP_MOVE_START));
      px = leg.stepX + (this.c.x - leg.stepX) * s;
      pz = leg.stepZ + (this.c.z - leg.stepZ) * s;
    } else {
      if (!leg.landed) { leg.landed = true; leg.landX = this.c.x; leg.landZ = this.c.z; }
      px = leg.landX; pz = leg.landZ;
    }
    const rise = Math.sin(Math.PI * u);
    const base = Math.max(0, lock.band[side] - 0.06);
    leg.stepLift = Math.max(0, base + leg.stepHeight * rise - rel);
    leg.offX = px - this.c.x; leg.offZ = pz - this.c.z;
    if (u < 1) return;
    leg.stepT = -1; leg.stepLift = 0;
    if (stance) {
      leg.locked = true; leg.lockX = px; leg.lockZ = pz;
      leg.turnLocked = leg.turnLocked || plant;
    } else { leg.locked = false; leg.turnLocked = false; }
  }

  /** 어느 발이든 옮겨 딛는 중인지(정리 걸음은 한 발씩) */
  private anyStepping(): boolean {
    for (const leg of this.legs) if (leg.stepT >= 0) return true;
    return false;
  }

  /** 엉덩이에서 (x,y,z)까지가 다리 길이(넓적다리+정강이)의 REACH배를 넘는지 */
  private overReach(leg: LegContact, x: number, y: number, z: number): boolean {
    leg.hip.getWorldPosition(this.a); leg.knee.getWorldPosition(this.b);
    const length = this.a.distanceTo(this.b) + this.b.distanceTo(this.c);
    return Math.hypot(x - this.a.x, y - this.a.y, z - this.a.z) > REACH * length;
  }

  private rotateWorld(bone: THREE.Bone): void {
    bone.getWorldQuaternion(this.rotation);
    bone.parent!.getWorldQuaternion(this.parentRotation);
    this.delta.setFromUnitVectors(this.from.normalize(), this.to.normalize());
    bone.quaternion.copy(this.parentRotation.invert().multiply(this.delta).multiply(this.rotation));
    bone.updateWorldMatrix(true, true);
  }

  private solve(leg: LegContact, lift: number, dx = 0, dz = 0): void {
    leg.hip.getWorldPosition(this.a); leg.knee.getWorldPosition(this.b); leg.foot.getWorldPosition(this.c);
    leg.foot.getWorldQuaternion(this.footWorld);
    const upper = this.a.distanceTo(this.b), lower = this.b.distanceTo(this.c);
    this.target.copy(this.c); this.target.x += dx; this.target.y += lift; this.target.z += dz;
    this.direction.subVectors(this.target, this.a);
    const requested = this.direction.length();
    if (requested < 1e-6 || upper < 1e-6 || lower < 1e-6) return;
    this.direction.multiplyScalar(1 / requested);
    const distance = Math.max(Math.abs(upper - lower) + 1e-5, Math.min(upper + lower - 1e-5, requested));
    this.target.copy(this.a).addScaledVector(this.direction, distance);
    // 무릎 굽힘 방향은 클립 다리(엉덩이→클립 발목 선)에서 무릎이 나간 방향을 새 다리 선에 수직으로 옮겨 쓴다.
    // 예전처럼 새 선(엉덩이→고정점) 기준으로만 재면 고정점이 클립 발에서 멀 때 무릎이 반대편으로 잡혀 -44도 역굽힘이 났다(2026-10-20).
    this.from.subVectors(this.c, this.a).normalize();
    this.bend.subVectors(this.b, this.a);
    this.bend.addScaledVector(this.from, -this.bend.dot(this.from));
    this.bend.addScaledVector(this.direction, -this.bend.dot(this.direction));
    // 펴진 무릎에 임의의 새 굽힘 방향을 주지 않는다.
    if (this.bend.lengthSq() < 1e-10) return;
    this.bend.normalize();
    const along = (upper * upper - lower * lower + distance * distance) / (2 * distance);
    const height = Math.sqrt(Math.max(0, upper * upper - along * along));
    this.desiredKnee.copy(this.a).addScaledVector(this.direction, along).addScaledVector(this.bend, height);
    this.from.subVectors(this.b, this.a); this.to.subVectors(this.desiredKnee, this.a); this.rotateWorld(leg.hip);
    leg.knee.getWorldPosition(this.b); leg.foot.getWorldPosition(this.c);
    this.from.subVectors(this.c, this.b); this.to.subVectors(this.target, this.b); this.rotateWorld(leg.knee);
    // 발끝 구름과 손/검 자세를 보존한다. Foot 아래의 원본 toe 회전은 바꾸지 않는다.
    leg.foot.parent!.getWorldQuaternion(this.parentRotation);
    leg.foot.quaternion.copy(this.parentRotation.invert().multiply(this.footWorld));
    leg.foot.updateWorldMatrix(true, true);
  }
}
